import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOutRedirect } from '../../auth/cognitoLogout';
import { useApiWithAuth } from '../../auth/apiHelper';
import { FaPlus, FaSignOutAlt, FaCalendarAlt, FaEdit, FaTrash } from 'react-icons/fa';
import styles from './adminEvents.module.scss';

type EventFormData = {
    title: string;
    eventDateTime: string;
    time: string;
    location: string;
    type: string;
    status: string;
    description: string;
    link: string;
    ourPresence: string;
    active: boolean;
};

type Event = EventFormData & {
    eventID: string;
    createdAt: string;
};

const AdminEventsPage = () => {
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [events, setEvents] = useState<Event[]>([]);
    const [eventsLoading, setEventsLoading] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const fetchWithAuth = useApiWithAuth();

    // CRA env var
    const apiUrl = useMemo(() => {
        const raw = process.env.REACT_APP_EVENTS_API || '';
        return raw.trim().replace(/\/$/, '');
    }, []);

    const [formData, setFormData] = useState<EventFormData>({
        title: '',
        eventDateTime: '',
        time: '',
        location: '',
        type: 'Conference',
        status: 'Attending',
        description: '',
        link: '',
        ourPresence: '',
        active: true
    });

    // Load all events on mount
    useEffect(() => {
        loadEvents();
    }, []);

    const loadEvents = async () => {
        try {
            setEventsLoading(true);
            const response = await fetch(`${apiUrl}/events`);
            if (!response.ok) throw new Error('Failed to load events');
            const data = await response.json();
            
            const items = Array.isArray(data) ? data : [];

            setEvents(
                items.map((item: Event) => ({
                    ...item,
                    eventDateTime: normalizeDateOnly(item.eventDateTime),
                }))
            );
        } catch (error) {
            console.error('Error loading events:', error);
        } finally {
            setEventsLoading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleActiveToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({ ...prev, active: e.target.checked }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSuccessMessage('');
        setErrorMessage('');

        try {      
            const payload = {
                ...formData,
                eventID: editingId || `event-${Date.now()}`,
                eventDateTime: normalizeDateOnly(formData.eventDateTime),
                datePassed: new Date(`${formData.eventDateTime}T12:00:00`) < new Date()
            };
                  
            const response = await fetchWithAuth(`${apiUrl}/events`, {
                method: `POST`,
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error(`Failed to ${editingId ? 'update' : 'create'} event: ${response.statusText}`);
            }

            setSuccessMessage(`Event ${editingId ? 'updated' : 'created'} successfully!`);

            // Reset form
            setFormData({
                title: '',
                eventDateTime: '',
                time: '',
                location: '',
                type: 'Conference',
                status: 'Attending',
                description: '',
                link: '',
                ourPresence: '',
                active: true
            });
            
            setEditingId(null);
            loadEvents();
            
            setTimeout(() => setSuccessMessage(''), 5000);
        } catch (error: any) {
            console.error('Error creating event:', error);
            setErrorMessage(error.message || 'Failed to create event');

        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEditEvent = (event: Event) => {
        setFormData({
            title: event.title,
            eventDateTime: normalizeDateOnly(event.eventDateTime),
            time: event.time,
            location: event.location,
            type: event.type,
            status: event.status,
            description: event.description,
            link: event.link,
            ourPresence: event.ourPresence,
            active: event.active
        });
        setEditingId(event.eventID);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCancelEdit = ()  => {
        setEditingId(null);
        setFormData({
            title: '',
            eventDateTime: '',
            time: '',
            location: '',
            type: 'Conference',
            status: 'Attending',
            description: '',
            link: '',
            ourPresence: '',
            active: true
        });
    };

    const handleDeleteEvent = async (event: Event) => {
        const confirmed = window.confirm(`Delete "${event.title}"? This action cannot be undone.`);
        if (!confirmed) return;

        setErrorMessage('');
        setSuccessMessage('');
        setDeletingId(event.eventID);

        try {
            const qs = new URLSearchParams({
                eventID: event.eventID,
                eventDateTime: event.eventDateTime,
            }).toString();

            const response = await fetchWithAuth(`${apiUrl}/events?${qs}`, {
                method: 'DELETE',
            });

            const raw = await response.text();

            if (!response.ok) {
                let msg = `Failed to delete event (${response.status})`;
                try {
                    const parsed = JSON.parse(raw);
                    if (parsed?.message) msg = parsed.message;
                } catch {}
                throw new Error(msg);
            }

            setSuccessMessage('Event deleted successfully!');
            await loadEvents();
            setTimeout(() => setSuccessMessage(''), 5000);
        } catch (error: any) {
            console.error('Error deleting event:', error);
            setErrorMessage(error.message || 'Failed to delete event');
        } finally {
            setDeletingId(null);
        }
    };
    
    const parseDateOnly = (value?: string): string => {
        if (!value) return '';
        const datePart = value.split('T')[0];
        const [year, month, day] = datePart.split('-').map(Number);
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    };

    // date string
    const normalizeDateOnly = (value?: string): string => {
        if (!value) return '';
        return value.split('T')[0];
    };
    
    // Other helpers for fetching event details
    const eventDate = (event: Event) => {
        const parsed = parseDateOnly(event.eventDateTime);
        return parsed ? new Date(`${parsed}T12:00:00`) : new Date(NaN);
    };

    return (
        <div className={styles.adminPage}>

            <div className={styles.adminContent}>
                <div className={styles.container}>
                    <div className={styles.adminHeader}>
                    <h1><FaCalendarAlt /> Event Management</h1>
                    <button onClick={signOutRedirect} className={styles.logoutBtn}>
                        <FaSignOutAlt /> Logout
                    </button>
                </div>

                {successMessage && (
                    <div className={styles.successAlert}>{successMessage}</div>
                )}

                {errorMessage && (
                    <div className={styles.errorAlert}>{errorMessage}</div>
                )}

                <div className={styles.formCard}>
                    <h2><FaPlus /> {editingId ? 'Edit Event' : 'Add New Event'}</h2>

                    <form onSubmit={handleSubmit}>
                        <div className={styles.formGrid}>
                            <div className={styles.formGroup}>
                                <label htmlFor="title">Event Title *</label>
                                <input
                                    id="title"
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleInputChange}
                                    required
                                    placeholder="ServiceNow Knowledge 2026"
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="eventDateTime">Event Date *</label>
                                <input
                                    id="eventDateTime"
                                    type="date"
                                    name="eventDateTime"
                                    value={formData.eventDateTime}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="time">Time</label>
                                <input
                                    id="time"
                                    type="text"
                                    name="time"
                                    value={formData.time}
                                    onChange={handleInputChange}
                                    placeholder="9:00 AM - 6:00 PM"
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="location">Location *</label>
                                <input
                                    id="location"
                                    type="text"
                                    name="location"
                                    value={formData.location}
                                    onChange={handleInputChange}
                                    required
                                    placeholder="Las Vegas, NV"
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="type">Event Type *</label>
                                <select
                                    id="type"
                                    name="type"
                                    value={formData.type}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="Conference">Conference</option>
                                    <option value="Webinar">Webinar</option>
                                    <option value="Summit">Summit</option>
                                    <option value="Workshop">Workshop</option>
                                    <option value="Meetup">Meetup</option>
                                </select>
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="status">Our Status *</label>
                                <select
                                    id="status"
                                    name="status"
                                    value={formData.status}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="Hosting">Hosting</option>
                                    <option value="Speaking">Speaking</option>
                                    <option value="Attending">Attending</option>
                                    <option value="Sponsoring">Sponsoring</option>
                                </select>
                            </div>
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="link">Event Link</label>
                            <input
                                id="link"
                                type="url"
                                name="link"
                                value={formData.link}
                                onChange={handleInputChange}
                                placeholder="https://knowledge.servicenow.com"
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="description">Description *</label>
                            <textarea
                                id="description"
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                required
                                rows={4}
                                placeholder="Brief description of the event..."
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="ourPresence">Our Presence *</label>
                            <textarea
                                id="ourPresence"
                                name="ourPresence"
                                value={formData.ourPresence}
                                onChange={handleInputChange}
                                required
                                rows={3}
                                placeholder="Booth #245, Speaking Session on ITSM Best Practices"
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="active" className={styles.checkboxLabel}>
                                <input
                                    id="active"
                                    type="checkbox"
                                    name="active"
                                    checked={formData.active}
                                    onChange={handleActiveToggle}
                                />
                                <span>Active (Display on public Events page)</span>
                            </label>
                        </div>

                        <div className={styles.formActions}>
                            <button
                                type="submit"
                                className={styles.submitBtn}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Creating...' : <><FaPlus /> Create Event</>}
                            </button>
                            {editingId && (
                                <button
                                    type="button"
                                    className={styles.cancelBtn}
                                    onClick={handleCancelEdit}
                                >
                                    Cancel
                                </button>
                            )}
                        </div>
                    </form>
                </div>

                {/* Events List */}
                <div className={styles.eventsListCard}>
                    <h2>All Events</h2>
                    {eventsLoading && <p>Loading events...</p>}
                    {events.length === 0 && !eventsLoading && <p>No events found.</p>}

                    <div className={styles.eventsTable}>
                        <table>
                            <thead>
                                <tr>
                                    <th>Title</th>
                                    <th>Date</th>
                                    <th>Type</th>
                                    <th>Active</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {events.map(event => (
                                    <tr key={event.eventID}>
                                        <td className={styles.eventTitle}>{event.title}</td>
                                        <td>{event.eventDateTime}</td>
                                        <td>{event.type}</td>
                                        <td>{event.status}</td>
                                        <td>
                                            <span className={`${styles.activeBadge} ${event.active ? styles.active : styles.inactive}`}>
                                                {event.active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td>
                                            <button
                                                className={styles.editBtn}
                                                onClick={() => handleEditEvent(event)}
                                                title="Edit event"
                                            >
                                                <FaEdit /> Edit
                                            </button>

                                            <button
                                                className={styles.deleteBtn}
                                                onClick={() => handleDeleteEvent(event)}
                                                title="Delete Event"
                                                disabled={deletingId === event.eventID}
                                            >
                                                <FaTrash /> {deletingId === event.eventID ? 'Deleting...' : 'Delete'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                </div>
            </div>

        </div>
    );
};

export default AdminEventsPage;